import S, { DataSignal } from 's-js';
import SArray, { lift } from 's-array';
import { IAjax } from 'lib/Ajax';
import { AutoSavingDraftObject } from 'lib/autoSave';
import { ValidationWatcher } from 'lib/validationWatcher';
import nonuniqueIndex from 'lib/nonuniqueIndex';
import { Entrypoints } from 'core/app/entrypoints';
import { PCCache } from 'data/PCCache';
import { Berth } from 'data/models/Berth';
import { Organization } from 'data/models/Organization';
import { PCUser } from 'data/models/PCUser';
import { PierGroup } from 'data/models/PierGroup';
import { PhoneNumber, PhoneNumberData } from 'data/models/PhoneNumber';
import { Port } from 'data/models/Port';
import { PostalAddress, PostalAddressData } from 'data/models/PostalAddress';
import { OrgProfileResponseData } from 'data/models/OrgProfileResponse';
import { OrgProfileCache } from './cache';
import { AddBerth, AddBerthParams } from 'common/models/AddBerth';
import { SelectProfileImage, SelectProfileImageParams } from 'portcall/selectprofileimage/model';
import { PhoneNumber as PhoneNumberViewModel } from 'portcall/phonenumber/model';
import { PostalAddress as PostalAddressViewModel } from 'portcall/postaladdress/model';
import { PersonProfilePopup, PersonProfilePopupParams } from 'portcall/personprofilepopup/model';
import { InviteUsers, InviteUsersParams } from 'portcall/inviteusers/model';
import { UserPermissions, UserPermissionsParams } from 'portcall/userpermissions/model';

export class OrgProfileParams {
    constructor(
        public id : number = 0
    ) { }
}

export interface OrganizationViewModel {
    Model : Organization;
    Validation : ValidationWatcher;
    ShowSelectProfileImage : DataSignal<boolean>;
    ImageSelector : () => SelectProfileImage | null;
    Users : () => PCUserViewModel[];
    PhoneNumbers : PhoneNumbersViewModel;
    PostalAddresses : PostalAddressesViewModel;
}

export interface PCUserViewModel {
    Model : PCUser;
    ToggleProfile : () => void;
    ProfilePopup : () => PersonProfilePopup | null;
}

export interface PortViewModel {
    Model : Port;
    PierGroups : () => PierGroupViewModel[];
}

export interface PierGroupViewModel {
    Model : PierGroup;
    Berths : () => Berth[];
}

export interface PostalAddressesViewModel {
    Addresses : () => PostalAddressViewModel[];
    Add : () => void;
}

export interface PhoneNumbersViewModel {
    PhoneNumbers : () => PhoneNumberViewModel[];
    Add : () => void;
}

export function OrgProfile(
    entrypoints : Entrypoints,
    ajax : IAjax,
    cache : PCCache,
    queryCache : OrgProfileCache,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    inviteUsersModel : (params : InviteUsersParams) => InviteUsers,
    userPermissionsModel : (params : UserPermissionsParams) => UserPermissions,
    selectProfileImageModel : (params : SelectProfileImageParams) => SelectProfileImage,
    phoneNumberModel : (pn : PhoneNumber, deleteFn : (ts : Date) => void) => PhoneNumberViewModel,
    postalAddressModel : (addr : PostalAddress, deleteFn? : (ts : Date) => void) => PostalAddressViewModel,
    addBerthModel : (params : AddBerthParams) => AddBerth,
    params : OrgProfileParams
) {
    let model = queryCache.OrgProfileResponses.update1(null, { OrganizationId: params.id }),
        url = entrypoints.OrgProfile({ Id: params.id }),
        request = ajax.Url<OrgProfileResponseData>(url).Get(),
        orgContainer = S(() => model.Organization() && OrganizationViewModel(model.Organization()!)),
        editing = S.data(false),
        berths = SArray<Berth>([]),
        berthsByPierGroup = nonuniqueIndex(berths, b => b.PierGroupId()),
        piergroups = model.PierGroups.map(PierGroupViewModel),
        pierGroupsByPort = nonuniqueIndex(piergroups, pg => pg.Model.PortId()),
        ports = model.Ports.sort((p1, p2) => p1.Order() - p2.Order()).map(PortViewModel),
        showInviteUsers = S.data(false),
        inviteUsers = S(() => showInviteUsers() ? inviteUsersModel({ selectedOrganizationId: params.id, onHidePopup: () => showInviteUsers(false) }) : null),
        showUserPermissions = S.data(false),
        userPermissions = S(() => showUserPermissions() ? userPermissionsModel({ organizationId: params.id, onHidePopup: () => showUserPermissions(false) }) : null),
        addBerth = addBerthModel({ SchedulingAuthorityId: params.id });

    S(() => {
        if (model.Organization()) {
            berths(model.Organization()!.ManagedBerths());
        } else {
            berths([]);
        }
    });

    request.done(data => {
        queryCache.OrgProfileResponses.update1(request.Date, data);
    });

    return {
        Id: params.id,
        Request: request,
        Editing: editing,
        Model: model,
        Organization: orgContainer,
        Berths: berths,
        Ports: ports,
        InviteUsers: inviteUsers,
        ShowInviteUsers: showInviteUsers,
        UserPermissions: userPermissions,
        ShowUserPermissions: showUserPermissions,
        AddBerth: addBerth,
        GoBack: () => window.history.back()
    };

    function OrganizationViewModel(organization : Organization) : OrganizationViewModel {
        let client = ajax.Url<Organization>(() => organization.Url() || ''),
            draft = AutoSavingDraftObject(organization, client),
            validation = ValidationWatcher(client),
            showSelectProfileImage = S.data(false),
            imageSelector = S(() => showSelectProfileImage() ? ConstructSelectProfileImage() : null),
            phoneNumbersVM = PhoneNumbersViewModel(organization),
            postalAddressesVM = PostalAddressesViewModel(organization),
            users = organization.Users.filter(u => u.Active()).map(PCUserViewModel);
        
        return {
            Model: draft,
            Validation: validation,
            ImageSelector: imageSelector,
            ShowSelectProfileImage: showSelectProfileImage,
            Users: users,
            PhoneNumbers: phoneNumbersVM,
            PostalAddresses: postalAddressesVM
        };

        function ConstructSelectProfileImage() {
            editing(false);
            return selectProfileImageModel({
                image: organization.Image,
                allowedImageUploadExtensions: model.Ref.AllowedImageUploadExtensions,
                maximumImageUploadFileSize: model.Ref.MaximumImageUploadFileSize,
                defaultImages: model.Ref.DefaultImages,
                minimumImageUploadImageSize: model.Ref.MinimumImageUploadImageSize,
                saveCroppedUploadUrl: entrypoints.SaveOrganizationImageUpload({ Id: params.id }),
                saveImageSelection: function () {
                    client.Put(organization);
                },
                onHide: () => showSelectProfileImage(false)
            });
        }
    }

    function PCUserViewModel(user : PCUser) : PCUserViewModel {
        let showProfile = S.data(false),
            profilePopup = S(() => showProfile() ? personProfilePopupModel({ id: user.Id(), onHidePopup: () => showProfile(false) }) : null);

        return {
            Model: user,
            ToggleProfile: () => showProfile(!showProfile()),
            ProfilePopup: profilePopup
        };
    }

    function PortViewModel(port : Port) : PortViewModel {
        let piergroups = lift(pierGroupsByPort(port.Id)).orderBy(p => p.Model.Order());

        return {
            Model: port,
            PierGroups: piergroups
        };
    }

    function PierGroupViewModel(pg : PierGroup) : PierGroupViewModel {
        let berths = lift(berthsByPierGroup(pg.Id)).sort(Berth.StartMarkOrder);

        return {
            Model: pg,
            Berths: berths
        };
    }

    function PostalAddressesViewModel(organization : Organization) : PostalAddressesViewModel {
        let client = ajax.Url<PostalAddress, PostalAddressData>(() => organization.PostalAddressesUrl() || '');

        return {
            Addresses: organization.PostalAddresses.map(addr => postalAddressModel(addr, ts => RemoveFromCache(addr, ts))),
            Add: Add
        };

        function Add() {
            let req = client.Post(new PostalAddress());
            req.done(data => {
                cache.PostalAddresses.update1(req.Date, data);
            });
        }

        function RemoveFromCache(addr : PostalAddress, ts : Date) {
            let id = S.sample(() => PostalAddress.key(addr));
            cache.PostalAddresses.update(ts, [], [id]);
        }
    }

    function PhoneNumbersViewModel(organization : Organization) : PhoneNumbersViewModel {    
        let client = ajax.Url<PhoneNumber, PhoneNumberData>(() => organization.PhoneNumbersUrl() || '');

        return {
            PhoneNumbers: organization.PhoneNumbers.map(pn => phoneNumberModel(pn, ts => RemoveFromCache(pn, ts))),
            Add: Add
        };

        function Add() {
            let req = client.Post(new PhoneNumber());
            req.done(data => {
                cache.PhoneNumbers.update1(req.Date, data);
            });
        }

        function RemoveFromCache(pn : PhoneNumber, ts : Date) {
            let id = S.sample(() => PhoneNumber.key(pn));
            cache.PhoneNumbers.update(ts, [], [id]);
        }
    }
}
export type OrgProfile = ReturnType<typeof OrgProfile>;



// WEBPACK FOOTER //
// ./apps/portcall/orgprofile/model.ts